import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Printer, ChevronLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { api, formatINR, formatNum, resolveImg, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { GOLD_STATUS_LABELS, GOLD_STATUS_STYLE, RUPEE_STATUS_LABELS, RUPEE_STATUS_STYLE } from "@/pages/admin/orders/orderConstants";
import { fmtGrams } from "@/pages/admin/orders/orderConstants";
import { hasAdminSession } from "@/lib/session";

const STATUS_LABELS = {
  pending_approval: "Pending Approval", approved: "Approved", processing: "Processing",
  shipped: "Shipped", completed: "Completed", rejected: "Rejected", cancelled: "Cancelled",
};

const addressLine = (addr) => {
  if (!addr) return "";
  return [addr.line1, addr.line2, [addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")].filter(Boolean).join(", ");
};

export default function Invoice() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [biz, setBiz] = useState({});

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get(`/crm/orders/${orderId}`).then(({ data }) => setOrder(data));
    api.get("/crm/business").then(({ data }) => setBiz(data)).catch(() => {});
    // eslint-disable-next-line
  }, [orderId]);

  useEffect(() => {
    if (order && searchParams.get("print") === "1") {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [order, searchParams]);

  if (!order) return <div className="py-24 text-center text-muted-foreground">Loading...</div>;

  const tax = order.tax_summary;
  const grand = order.total;
  let taxable, gstAmt, cgst, sgst, igst, gstPct;
  if (tax) {
    gstPct = tax.gst_pct || 3; taxable = tax.taxable_value; gstAmt = tax.gst_amount;
    cgst = tax.cgst_amount || 0; sgst = tax.sgst_amount || 0; igst = tax.igst_amount || 0;
  } else {
    gstPct = order.items?.[0]?.pricing_snapshot?.gst_pct || 3;
    taxable = Math.round(grand / (1 + gstPct / 100));
    gstAmt = grand - taxable;
    cgst = sgst = Math.round(gstAmt / 2); igst = 0;
  }
  const billingAddr = addressLine(order.billing_address_snapshot);
  const shippingAddr = order.shipping_address_snapshot?.same_as_billing === false ? addressLine(order.shipping_address_snapshot) : "";
  const hsnCode = order.hsn_code_snapshot || biz.default_hsn_code || "7113";

  const addr = [biz.address_line1, biz.address_line2, [biz.city, biz.state, biz.pincode].filter(Boolean).join(", ")].filter(Boolean);

  const itemSpecs = (snapshot) => {
    if (!snapshot) return null;
    const g = snapshot.gold || {};
    const diamonds = snapshot.diamonds || [];
    const diamondWt = diamonds.reduce((s, d) => s + (Number(d.carat) || 0), 0);
    // Effective (post-discount/markup) diamond value — falls back to the raw value for older
    // orders saved before this field existed on the pricing snapshot.
    const diamondVal = diamonds.reduce((s, d) => s + (Number(d.effective_value ?? d.value) || 0), 0);
    const avgDiamondRate = diamondWt > 0 ? diamondVal / diamondWt : 0;
    const hasGold = (g.weight_g || 0) > 0;
    // Combined into a single figure (rather than two rows) to match the storefront Price
    // Breakdown — these are still added into the item's Amount, so they must be shown here too,
    // otherwise the specs line looks like it doesn't add up to the invoice total.
    const certHallmarkTotal = (snapshot.certification_charges || 0) + (snapshot.hallmarking_charges || 0);
    if (!hasGold && diamondWt <= 0 && certHallmarkTotal <= 0) return null;
    return {
      gross: g.gross_weight_g || g.weight_g || 0,
      net: g.weight_g || 0,
      karat: g.karat || "",
      ratePerGram: g.rate_per_gram_24k || 0,
      making: g.making_value || 0,
      diamondWt,
      avgDiamondRate,
      certHallmarkTotal,
      hasGold,
    };
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4 no-print">
          <Button variant="ghost" onClick={() => navigate("/crm/orders")} className="gap-1"><ChevronLeft className="h-4 w-4" /> Back to Orders</Button>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-invoice-pdf-button" variant="outline" className="gap-2"
              onClick={() => downloadBlob(`/crm/orders/${orderId}/invoice.pdf`, `Invoice-${order.order_no}.pdf`).catch(() => toast.error("Download failed"))}>
              <Download className="h-4 w-4" /> Download PDF
            </Button>
            <Button data-testid="admin-invoice-print-button" onClick={() => window.print()} className="gap-2"><Printer className="h-4 w-4" /> Print Invoice</Button>
          </div>
        </div>

        <div className="print-page bg-white rounded-xl border border-border p-8 card-shadow-soft" data-testid="invoice-content">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {biz.logo_url ? <img src={resolveImg(biz.logo_url)} alt="logo" className="h-12 w-12 rounded object-cover" /> : null}
              <div>
                <div className="font-display text-2xl text-[hsl(var(--primary))]">{biz.company_name || "MAAHEYJEWELS"}</div>
                {addr.map((l, i) => <p key={`addr-${i}-${l}`} className="text-xs text-muted-foreground">{l}</p>)}
                <p className="text-xs text-muted-foreground mt-1">
                  {biz.gstin ? <>GSTIN: <span className="font-mono">{biz.gstin}</span></> : "GSTIN: —"}
                  {biz.phone ? <> · {biz.phone}</> : ""}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-xl">TAX INVOICE</div>
              <div data-testid="invoice-order-no" className="font-mono text-sm mt-1">{order.invoice_no || `(Draft) ${order.order_no}`}</div>
              {order.invoice_date ? (
                <>
                  <div className="text-xs text-muted-foreground">Invoice Date: {order.invoice_date}</div>
                  {order.due_date && <div className="text-xs text-muted-foreground">Due Date: {order.due_date}</div>}
                </>
              ) : (
                <div className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString("en-IN")}</div>
              )}
            </div>
          </div>
          <Separator className="my-5" />
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Billed To</div>
              <div className="font-medium">{order.shop_name}</div>
              <div className="font-mono text-xs">{order.shop_phone}</div>
              {order.shop_gstin && <div className="font-mono text-xs">GSTIN: {order.shop_gstin}</div>}
              {billingAddr && <div className="text-xs text-muted-foreground mt-0.5">{billingAddr}</div>}
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Shipped To</div>
              <div className="text-xs text-muted-foreground">{shippingAddr || billingAddr || "Same as billing"}</div>
            </div>
            <div className="text-right"><div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">Status</div><div className="font-medium">{STATUS_LABELS[order.status] || order.status}</div></div>
          </div>
          <Separator className="my-5" />
          <p className="text-xs text-muted-foreground mb-2">HSN/SAC Code: <span className="font-mono">{hsnCode}</span></p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Item</th><th className="py-2 text-center">Qty</th><th className="py-2 text-right">Unit Price</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody>
              {order.items.map((it, i) => {
                const specs = itemSpecs(it.pricing_snapshot);
                return (
                <tr key={it.product_id || it.sku || i} className="border-b border-border">
                    <td className="py-3">
                      <div className="font-medium">{it.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{it.sku}</div>
                      {specs && (
                        <div data-testid="invoice-item-specs" className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                          {specs.hasGold && specs.gross > 0 && <span>Gross Wt: <span className="font-mono text-foreground">{specs.gross}g</span></span>}
                          {specs.hasGold && specs.net > 0 && <span>Net Wt: <span className="font-mono text-foreground">{specs.net}g</span></span>}
                          {specs.hasGold && specs.karat && <span>Purity: <span className="font-mono text-foreground">{specs.karat}</span></span>}
                          {specs.hasGold && specs.ratePerGram > 0 && <span>Gold Rate/g: <span className="font-mono text-foreground">{formatINR(specs.ratePerGram)}</span></span>}
                          {specs.hasGold && specs.making > 0 && <span>Labour: <span className="font-mono text-foreground">{formatINR(specs.making)}</span></span>}
                          {specs.diamondWt > 0 && <span>Diamond Wt: <span className="font-mono text-foreground">{specs.diamondWt.toFixed(2)}ct</span></span>}
                          {specs.avgDiamondRate > 0 && <span>Net Diamond Rate: <span className="font-mono text-foreground">{formatINR(specs.avgDiamondRate)}/ct</span></span>}
                          {specs.certHallmarkTotal > 0 && <span>Hallmarking & Certification: <span className="font-mono text-foreground">{formatINR(specs.certHallmarkTotal)}</span></span>}
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-center font-mono">{it.quantity}</td>
                    <td className="py-3 text-right font-mono">{formatINR(it.unit_price)}</td>
                    <td className="py-3 text-right font-mono">{formatINR(it.line_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex justify-end mt-5">
            <div className="w-72 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Taxable Value</span><span className="font-mono">{formatINR(taxable)}</span></div>
              {igst > 0 ? (
                <div className="flex justify-between"><span className="text-muted-foreground">IGST @ {formatNum(gstPct)}%</span><span className="font-mono">{formatINR(igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">CGST @ {formatNum(gstPct / 2)}%</span><span className="font-mono">{formatINR(cgst)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">SGST @ {formatNum(gstPct / 2)}%</span><span className="font-mono">{formatINR(sgst)}</span></div>
                </>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between text-base"><span className="font-display">Grand Total</span><span data-testid="invoice-grand-total" className="font-mono text-[hsl(var(--primary))]">{formatINR(grand)}</span></div>
            </div>
          </div>
          <Separator className="my-5" />
          {order.invoice_no && (
            <div data-testid="invoice-payment-status" className="rounded-lg border border-border bg-[#F7F0E6] p-4 mb-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Rupee Status</span>
                <Badge className={RUPEE_STATUS_STYLE[order.rupee_status] || ""}>{RUPEE_STATUS_LABELS[order.rupee_status] || order.rupee_status}</Badge>
                {order.gold_payable_fine_g > 0 && (
                  <>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground ml-2">Metal Status</span>
                    <Badge data-testid="invoice-metal-status" className={GOLD_STATUS_STYLE[order.gold_status] || ""}>{GOLD_STATUS_LABELS[order.gold_status] || order.gold_status}</Badge>
                  </>
                )}
                {order.fully_settled && <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">Fully Settled</Badge>}
              </div>
              <div className="text-sm flex items-center gap-4 flex-wrap">
                <span>Rupee Paid: <span className="font-mono text-[#1F5B2E]">{formatINR(order.amount_paid)}</span></span>
                {order.credit_notes_total > 0 && <span>Credit Notes: <span className="font-mono">{formatINR(order.credit_notes_total)}</span></span>}
                <span>Rupee Balance Due: <span className="font-mono text-[#7A1F1F]">{formatINR(order.outstanding_amount)}</span></span>
                {order.payment_terms_days ? <span className="text-muted-foreground text-xs">Terms: Net {order.payment_terms_days} days</span> : null}
              </div>
              {order.gold_payable_fine_g > 0 && (
                <div className="text-sm flex items-center gap-4 flex-wrap">
                  <span>Gold Received: <span className="font-mono text-[#1F5B2E]">{fmtGrams(order.gold_paid_fine_g)}</span></span>
                  <span>Gold Owed: <span className="font-mono">{fmtGrams(order.gold_payable_fine_g)}</span></span>
                  <span>Gold Balance Due: <span className="font-mono text-[#7A1F1F]">{fmtGrams(order.gold_outstanding_fine_g)}</span></span>
                </div>
              )}
            </div>
          )}
          {order.shipping && (order.shipping.courier_name || order.shipping.tracking_number) && (
            <div data-testid="invoice-shipping-info" className="text-sm text-muted-foreground mb-4">
              Shipped via <span className="text-foreground font-medium">{order.shipping.courier_name || "courier"}</span>
              {order.shipping.tracking_number && <> · Tracking: <span className="font-mono text-foreground">{order.shipping.tracking_number}</span></>}
            </div>
          )}
          {(biz.bank_name || biz.account_number || biz.upi_id) && (
            <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 mb-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Payment Details</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {biz.bank_name && <div><span className="text-muted-foreground">Bank: </span>{biz.bank_name}</div>}
                {biz.account_name && <div><span className="text-muted-foreground">A/c Name: </span>{biz.account_name}</div>}
                {biz.account_number && <div><span className="text-muted-foreground">A/c No: </span><span className="font-mono">{biz.account_number}</span></div>}
                {biz.ifsc && <div><span className="text-muted-foreground">IFSC: </span><span className="font-mono">{biz.ifsc}</span></div>}
                {biz.branch && <div><span className="text-muted-foreground">Branch: </span>{biz.branch}</div>}
                {biz.upi_id && <div><span className="text-muted-foreground">UPI: </span><span className="font-mono">{biz.upi_id}</span></div>}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">{biz.invoice_notes || "This is a computer-generated wholesale tax invoice. Prices include itemised gold, making, wastage, diamond and colour-stone values with GST."}</p>
        </div>
      </div>
    </div>
  );
}
